// src/features/stock/components/StockManagment.tsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import MinimalisticTable, { ColumnDef } from '../../../../shared/components/ui/MinimalisticTable';
import {
  listLocationsApi,
  createLocationApi,
  Location,
  CreateLocationPayload,
} from '../api/stockApi';

export default function StockManagment() {
  const queryClient = useQueryClient();

  // 1) Fetch locations
  const {
    data: locations = [],
    isLoading,
    error,
  } = useQuery<Location[], Error>({
    queryKey: ['locations'],
    queryFn: listLocationsApi,
    staleTime: 60_000,
  });

  // 2) Mutation to create a new location
  const createMutation = useMutation<
    { location: Location; execution_time_ms: number },
    Error,
    CreateLocationPayload
  >({
    mutationFn: (payload) => createLocationApi(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setIsAdding(false);
      setNewName('');
    },
  });

  // 3) Local state for inline new-row
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');

  const handleNew = () => {
    setIsAdding(true);
    setNewName('');
  };

  const handleAcceptNew = () => {
    const name = newName.trim();
    if (!name) return;
    createMutation.mutate({ name });
  };

  const handleCancelNew = () => {
    setIsAdding(false);
    setNewName('');
  };

  // 4) Loading / error states
  if (isLoading) {
    return <div className="text-sm text-neutral-400">Loading locations…</div>;
  }
  if (error) {
    return <div className="text-sm text-rose-400">Error: {error.message}</div>;
  }

  // 5) Table columns
  type LocationRow = Location & { __isNew?: boolean };

  const columns: ColumnDef<LocationRow>[] = [
    { header: 'ID', accessor: 'id' },
    { header: 'Name', accessor: 'name' },
  ];

  // 6) Table data including optional new-row
  const tableData: LocationRow[] = isAdding
    ? ([{ id: 0, name: '', __isNew: true }] as LocationRow[]).concat(locations)
    : locations;

  return (
    <div className="bg-neutral-900 p-4 rounded-md">
      <MinimalisticTable<LocationRow>
        columns={columns}
        data={tableData}
        noDataMessage="No locations found."
        showNewButton={!isAdding}
        onNew={handleNew}

        editableAccessors={['name']}
        newRowValues={{ name: newName }}
        onNewRowChange={(accessor, val) => {
          if (accessor === 'name') setNewName(val);
        }}

        onAcceptNew={handleAcceptNew}
        onCancelNew={handleCancelNew}
      />
    </div>
  );
}
